// src/components/OrgFileCard.jsx
import React, { useState } from "react";
import FileDropZone from "./FileDropZone";

const API = process.env.REACT_APP_API_URL;

/**
 * Props:
 * - header: title above the card (e.g. "Essensplan")
 * - fileId: current OrgFile ID (null if not set yet)
 * - filePath: current relative file path (string or null)
 * - onChangeFilePath: cb(newPathOrNull)
 * - onChangeFileId: cb(newIdOrNull)
 * - metaTitle: optional title saved with the file
 * - metaDescription: optional description saved with the file
 * - onMenuParsed: optional cb(menuJson) called with parsed weekly menu after upload
 */
export default function OrgFileCard({
  header = "Organisationsdatei",
  fileId,
  filePath,
  onChangeFilePath,
  onChangeFileId,
  metaTitle,
  metaDescription,
  onMenuParsed,
}) {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [err, setErr] = useState("");
  const [activeFrom, setActiveFrom] = useState("");
  const [activeUntil, setActiveUntil] = useState("");

  function localTodayISO() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  function isEndDateInPast(value) {
    return !!value && value < localTodayISO();
  }

  function validateDates() {
    if (!activeFrom || !activeUntil) {
      setErr("Bitte zuerst den Zeitraum (Gültig von/bis) auswählen.");
      return false;
    }
    if (activeFrom > activeUntil) {
      setErr("'Gültig von' darf nicht nach 'Gültig bis' liegen.");
      return false;
    }
    if (isEndDateInPast(activeUntil)) {
      setErr("'Gültig bis' darf nicht in der Vergangenheit liegen.");
      return false;
    }
    return true;
  }

  async function uploadFile(file) {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (metaTitle) fd.append("header", metaTitle);
      if (metaDescription) fd.append("description", metaDescription);
      fd.append("active_from", activeFrom);
      fd.append("active_until", activeUntil);

      const res = await fetch(`${API}/menu/plan`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();

      // Backend returns { file, menu } (or legacy flat shape)
      const uploadedFile = data.file || data;
      const menu = data.menu;

      onChangeFilePath?.(uploadedFile.path || null);
      if (uploadedFile.id && onChangeFileId) onChangeFileId(uploadedFile.id);
      if (menu && onMenuParsed) onMenuParsed(menu);

      setActiveFrom("");
      setActiveUntil("");
    } catch (e) {
      setErr(e?.message || "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove() {
    if (!fileId) return;
    setErr("");
    setRemoving(true);
    try {
      const res = await fetch(`${API}/orgfiles/${fileId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());

      onChangeFilePath?.(null);
      if (onChangeFileId) onChangeFileId(null);
    } catch (e) {
      setErr(e?.message || "Löschen fehlgeschlagen.");
    } finally {
      setRemoving(false);
    }
  }

  const prettyName = filePath ? filePath.split("/").pop() : "";
  const downloadHref = fileId ? `${API}/orgfiles/${fileId}` : null;

  return (
    <section className="rounded-xl border border-blue-300 border-dashed bg-blue-50/40 p-4">
      <h2 className="text-base font-semibold text-gray-900 mb-2">{header}</h2>

      {filePath && err && (
        <div role="alert" className="mb-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
          {err}
        </div>
      )}

      {filePath ? (
        // ✅ FILE EXISTS
        <div className="rounded-lg border border-blue-200 bg-white/70 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <svg viewBox="0 0 24 24" className="h-9 w-9 text-black/80 shrink-0">
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path d="M14 2v6h6" fill="none" stroke="currentColor" strokeWidth="1.5" />
              </svg>
              <div className="min-w-0">
                <span className="text-xl font-semibold text-gray-900 truncate">{prettyName}</span>
                {metaTitle && (
                  <div className="text-sm text-gray-700 truncate mt-0.5">{metaTitle}</div>
                )}
              </div>
            </div>

            <button
              onClick={handleRemove}
              disabled={removing}
              className="text-blue-600 hover:underline text-sm shrink-0"
            >
              {removing ? "Entferne…" : "Anhang entfernen"}
            </button>
          </div>

          <hr className="my-3 border-blue-200/70" />

          <div className="flex justify-end">
            {downloadHref ? (
              <a
                className="h-11 w-11 rounded-full bg-[#8EA2FF] hover:bg-[#7b8ef8] text-black/80 flex items-center justify-center shadow"
                href={downloadHref}
                target="_blank"
                rel="noreferrer"
                title="Herunterladen"
              >
                <svg viewBox="0 0 24 24" className="h-6 w-6">
                  <path
                    d="M12 3v11m0 0l4-4m-4 4l-4-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M5 20h14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </a>
            ) : (
              <div className="text-xs text-gray-600">Kein direkter Download verfügbar</div>
            )}
          </div>
        </div>
      ) : (
        // ✅ NO FILE → upload UI
        <FileDropZone
          onFile={uploadFile}
          onError={setErr}
          error={err}
          validate={validateDates}
          disabled={uploading}
          busyText="Essensplan wird verarbeitet…"
          className="flex flex-col items-center justify-center"
        >
          <div className="w-full max-w-md mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
              <label className="text-xs text-gray-700">
                Gültig von
                <input
                  type="date"
                  value={activeFrom}
                  onChange={(ev) => {
                    setActiveFrom(ev.target.value);
                    if (isEndDateInPast(activeUntil)) {
                      setErr("'Gültig bis' darf nicht in der Vergangenheit liegen.");
                    } else {
                      setErr("");
                    }
                  }}
                  className="mt-1 w-full rounded-md border border-blue-200 px-2 py-1.5 text-sm bg-white"
                />
              </label>
              <label className="text-xs text-gray-700">
                Gültig bis
                <input
                  type="date"
                  value={activeUntil}
                  onChange={(ev) => {
                    const v = ev.target.value;
                    setActiveUntil(v);
                    if (isEndDateInPast(v)) {
                      setErr("'Gültig bis' darf nicht in der Vergangenheit liegen.");
                    } else {
                      setErr("");
                    }
                  }}
                  className="mt-1 w-full rounded-md border border-blue-200 px-2 py-1.5 text-sm bg-white"
                />
              </label>
            </div>
          </div>

        </FileDropZone>
      )}
    </section>
  );
}
